import React, { Fragment, useEffect, useRef, useState } from "react";
import { Input, InputGroup, toaster, Toggle } from "rsuite";
import StatusControl from "../../../../shared/StatusControl/StatusControl";
import {
  GetControlBlockNtpDateTime_GetControlBlockNtpDateTimeResponse,
  GetNtpSynchronization_GetNtpSynchronizationResponse,
  SetNtpServer_SetNtpServerRequest,
} from "../../../../../api";
import { PrimaryClockApi } from "../../../../../services/PrimaryClock.api";
import NotificationFactory from "../../../../../shared/Notifications";
import moment, { Moment } from "moment";
import { BksApi } from "../../../../../services/Bks.api";
import {SynchronizationApi} from "../../../../../services/Synchronization.api";
import {TfiSaveIcon} from "../../../../../models/Icons.model";
import {SynchronizationNtpControlProps} from "./Synchronization.model";

const SynchronizationNtpControl = ({ clockStationId, onRefreshRef }: SynchronizationNtpControlProps) => {
  const [loading, setLoading] = useState(false);
  const [ownServerSynchronization, setOwnServerSynchronization] = useState(false);
  const [ntpServer, setNtpServer] = useState("");
  const [altNtpServer, setAltNtpServer] = useState("");
  const [ntpStatus, setNtpStatus] = useState(false);
  const [userTimezone, setUserTimezone] = useState<string | null>(null);
  const [ntpTimeUtc, setNtpTimeUtc] = useState("");
  const [localTime, setLocalTime] = useState("");
  const utcMomentRef = useRef<Moment | null>(null);
  const localMomentRef = useRef<Moment | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (onRefreshRef) {
      onRefreshRef.current = refreshAll;
    }
  }, [onRefreshRef, clockStationId]);

  useEffect(() => {
    refreshAll().then();
  }, [clockStationId]);

  useEffect(() => {
    if (!utcMomentRef.current || !userTimezone) {
      setNtpTimeUtc("");
      setLocalTime("");
      stopInterval();
      return;
    }

    setNtpTimeUtc(utcMomentRef.current.format("DD.MM.YYYY HH:mm:ss"));
    const offset = parseInt(userTimezone, 10) || 0;
    const local = utcMomentRef.current.clone().add(offset, "hours");
    localMomentRef.current = local;
    setLocalTime(local.format("DD.MM.YYYY HH:mm:ss"));

    stopInterval();
    intervalRef.current = setInterval(() => {
      if (!utcMomentRef.current) return;
      utcMomentRef.current.add(1, "seconds");
      setNtpTimeUtc(utcMomentRef.current.format("DD.MM.YYYY HH:mm:ss"));

      if (localMomentRef.current) {
        localMomentRef.current.add(1, "seconds");
        setLocalTime(localMomentRef.current.format("DD.MM.YYYY HH:mm:ss"));
      }
    }, 1000);

    return stopInterval;
  }, [userTimezone]);

  useEffect(() => stopInterval, []);

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const refreshAll = async () => {
    await readNtpData();
    await getNtpDateAndTime();
    await getBksTime();
  };

  const readNtpData = async () => {
    setLoading(true);
    try {
      const response: GetNtpSynchronization_GetNtpSynchronizationResponse = await SynchronizationApi.getNtpData(clockStationId);

      setNtpServer(response.server || "");
      setAltNtpServer(response.alternativeServer || "");
      setOwnServerSynchronization(!response.ownServer);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const setNtpData = async () => {
    setLoading(true);
    const payload: SetNtpServer_SetNtpServerRequest = {
      controlBlockId: clockStationId,
      ownServer: !ownServerSynchronization,
      server: ntpServer || null,
    };
    try {
      const result = await SynchronizationApi.updateNtpData(clockStationId, payload)

      if (result) {
        toaster.push(
          NotificationFactory.success(),
          { placement: "topEnd" }
        );
        await readNtpData();
      }
    } catch (error) {
      toaster.push(
        NotificationFactory.error(),
        { placement: "topEnd" }
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateAltNtpServer = async () => {
    setLoading(true);

    try {
      const result = await PrimaryClockApi.updateAltNtpServer(clockStationId as string, altNtpServer);

      if (result) {
        toaster.push(
          NotificationFactory.success(),
          { placement: "topEnd" }
        );
      }
    } catch (error) {
      console.error(error);
      toaster.push(
        NotificationFactory.error(),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const getNtpDateAndTime = async () => {
    setLoading(true);
    try {
      const result: GetControlBlockNtpDateTime_GetControlBlockNtpDateTimeResponse = await PrimaryClockApi.getNtpTime(clockStationId as string);

      if (result && result.time) {
        const mUtc = moment.utc(result.time);
        utcMomentRef.current = mUtc;
        setNtpStatus(Boolean(result.isNtpServerOnline));
      } else {
        utcMomentRef.current = null;
        setNtpStatus(false);
        setNtpTimeUtc("");
        setLocalTime("");
      }
    } catch (e) {
      console.error("Ошибка получения времени ntp:", e);
      toaster.push(
        NotificationFactory.error("Ошибка получения времени ntp"),
        { placement: "topEnd" }
      );
      utcMomentRef.current = null;
      setNtpStatus(false);
      setNtpTimeUtc("");
      setLocalTime("");
    } finally {
      setLoading(false);
    }
  };

  const getBksTime = async () => {
    setLoading(true);
    try {
      const response: Awaited<ReturnType<typeof BksApi.getTime>> = await BksApi.getTime(clockStationId as string);

      if (response?.time) {
        const utcMatch = response.time.match(/([+-])(\d{2}):\d{2}$/);
        if (utcMatch) {
          const sign = utcMatch[1];
          const hours = utcMatch[2];
          setUserTimezone(sign + hours);
        } else {
          setUserTimezone(null);
        }
      } else {
        setUserTimezone(null);
      }
    } catch (error) {
      console.error("Ошибка загрузки времени БКС", error);
      toaster.push(
        NotificationFactory.error("Ошибка получения времени БКС"),
        { placement: "topEnd" }
      );
      setUserTimezone(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <fieldset disabled={loading}>
        <div className="row my-3">
          <div className="col">
            <Toggle
              color="blue"
              checkedChildren="ON"
              unCheckedChildren="OFF"
              checked={ownServerSynchronization}
              onChange={(state) => {
                setOwnServerSynchronization(state);
                if (ownServerSynchronization) {
                  setNtpServer("ntp.bigclock.ru");
                }
              }}
            >
              Использовать сервер времени пользователя
            </Toggle>
          </div>
        </div>
        <div className="row mb-2">
          <div className="col-md-1 d-flex justify-content-center">
            <StatusControl status={ntpStatus ? "online" : "offline"} />
          </div>
          <div className="col-md-5">
            <InputGroup>
              <Input
                placeholder="Адрес сервера"
                disabled={!ownServerSynchronization}
                value={ntpServer}
                onChange={setNtpServer}
              />
              <InputGroup.Addon>
                <TfiSaveIcon
                  style={{ cursor: "pointer" }}
                  onClick={setNtpData}
                  title="Сохранить часовой пояс"
                />
              </InputGroup.Addon>
            </InputGroup>
          </div>
          <div className="col-md-6">
            <InputGroup className="mb-1">
              <InputGroup.Addon>Время UTC</InputGroup.Addon>
              <Input
                className="bg-yellow"
                disabled={true}
                value={ntpTimeUtc || ""}
              />
            </InputGroup>
          </div>
        </div>
        <div className="row mb-2">
          <div className="col-md-12">
            <InputGroup>
              <InputGroup.Addon>Альтернативный NTP сервер</InputGroup.Addon>
              <Input
                value={altNtpServer}
                onChange={setAltNtpServer}
              />
              <InputGroup.Addon>
                <TfiSaveIcon
                  style={{ cursor: "pointer" }}
                  onClick={updateAltNtpServer}
                  title="Сохранить часовой пояс"
                />
              </InputGroup.Addon>
            </InputGroup>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <InputGroup className="mb-1">
              <InputGroup.Addon>Время и дата</InputGroup.Addon>
              <Input
                className="bg-green"
                disabled={true}
                value={localTime || ""}
              />
            </InputGroup>
          </div>
          <div className="col-md-6">
            <InputGroup className="mb-1">
              <InputGroup.Addon>Час. пояс</InputGroup.Addon>
              <Input disabled={true} value={userTimezone || ""} />
            </InputGroup>
          </div>
        </div>
      </fieldset>
    </Fragment>
  );
};

export default SynchronizationNtpControl;
